

import * as React from "react";
import styles from "./Home.module.scss";

import myBookingsImg from "./assets/undraw_Hello_re_3evm.svg";
import bookDeskImg from "./assets/undraw_date_picker_gorr.svg"
import profileImg from "./assets/undraw_Profile_re_4a55.svg";
import settingsImg from "./assets/undraw_Setup_re_y9w8.svg";
import inOfficeImg from "./assets/undraw_in_the_office_re_jtgc.svg";
import approvals from "./assets/undraw_accept_request_re_d81h.svg";
import calendar from "./assets/undraw_schedule_re_2vro.svg";

import { getMyProfile, isAuthenticated } from "./api/userInfo";
import { IUserWithPhoto } from "./types/User.types";
import { DefaultButton, Dialog, DialogFooter, Persona, PrimaryButton, Spinner, Stack, TooltipHost } from "office-ui-fabric-react";
import { Callout } from "@fluentui/react";
import { ProfileEditorPanel } from "./components/profileEditor/ProfileEditorPanel";
import { SettingsPanel } from "./components/settings/SettingsPanel";
import { Route, RouteComponentProps, useHistory } from "react-router";
import { BookingDatePanel } from "./components/bookings/menus/BookingDatePanel";
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { BookingSitePanel } from "./components/bookings/menus/BookingSitePanel";
import { BookingLocationPanel } from "./components/bookings/menus/BookingLocationsPanel";
import { BookingFloorplanPanel } from "./components/bookings/menus/BookingFloorplansPanel";
import { DeskBookingForm } from "./components/bookings/forms/DeskBookingForm";
import { MyBookingsPanel } from "./components/bookings/menus/MyBookings";
import RoomBookingSearch from "./components/bookings/menus/RoomBookingSearch";
import { RoomBookingTop } from "./components/bookings/menus/RoomBookingTop";
import { getPendingBookingCount } from "./api/bookings";
import { ISettingsAndProfile } from "./types/Settings.types";
import { getSettings } from "./api/settings";
import { PendingApproval } from "./components/bookings/menus/PendingApproval";
import SiteCalendar from "./components/bookings/menus/SiteCalendar";
import { TagPicker } from "./components/TagPicker";
import { TagType } from "./types/Tagging.types";
import { CheckIn } from "./components/checkin";
import PushNotifications from "./pushNotifications";
import CacheBuster from "./components/cacheBuster";


export interface IHomeProps extends RouteComponentProps{}

export interface IHomeState {
    loadingProfile: boolean;
    profile?: IUserWithPhoto;
    showSignOut: boolean;
}

export const ProfileContext = React.createContext<ISettingsAndProfile|undefined>(undefined);

export const Home = (props: IHomeProps) => {

    const history = useHistory();
    const signOutDiv = useRef<HTMLDivElement | null>(null);
    
    const [ loadingProfile, setLoadingProfile ] = useState(true);
    const [ showSignOut, setShowSignOut ] = useState(false);
    const [ profileAndSettings, setProfileAndSettings ] = useState<ISettingsAndProfile|undefined>();
    const [pendingBookingCount, setPendingBookingCount] = useState(0);
    const [notificationsDefault, setNotificationsDefault] = useState<boolean>(false);
    const notifications = useMemo(() => {
        return new PushNotifications(() => {
            setNotificationsDefault(true);
        });
    }, []);

    const _loadProfile = async (abortController: AbortController) => {
        const loggedIn: boolean = await isAuthenticated();
        if (!loggedIn) window.location.href = window.location.protocol + '//' + window.location.host;
        const _profileAndSettings = await getSettings(abortController);
        setLoadingProfile(false);
        setProfileAndSettings(_profileAndSettings);
    };

    const _loadPendingBookings = useCallback(async (abortController: AbortController) => {
        const count = await getPendingBookingCount(abortController);
        if (!(count instanceof Error)) {
            setPendingBookingCount(count.PendingApprovals);
        } else {
            console.error(count);
        }
    }, [profileAndSettings]);

    useEffect(() => {
        let abortController:AbortController;
        if (profileAndSettings?.Profile?.RoomApprover) {
            setInterval(() => {
                abortController = new AbortController()
                _loadPendingBookings(abortController);
            }, 5000);
        }

        return () => {
            if (abortController) abortController.abort();
        }
    }, [profileAndSettings, _loadPendingBookings]);

    useEffect(() => {
        const abortController = new AbortController();
        _loadProfile(abortController);

        return () => {
            abortController.abort();
        }
    }, []);

    if (!profileAndSettings) {
        return (
            <Spinner label="Loading profile" />
        );
    }
    
    return (
        <CacheBuster>
            <ProfileContext.Provider value={{ ...profileAndSettings, onProfileUpdated: _loadProfile }}>
                <div className={styles.homepage}>
                    <div>
                        <Route path="/menu" exact={false}>
                            <div className={styles.homepageGrid}>
                                <div className={styles.logoRow}>
                                    <div ref={signOutDiv} className={styles.signOut}>
                                        { loadingProfile === true && (
                                            <Spinner />
                                        )}
                                        { profileAndSettings?.Profile !== undefined && (
                                            <Persona 
                                                text={profileAndSettings.Profile.FullName} secondaryText={profileAndSettings.Profile.Organisation} 
                                                imageUrl={profileAndSettings.Profile.Photo !== null ? `data:${profileAndSettings.Profile.Photo.Type};base64,${profileAndSettings.Profile.Photo.Data}` : undefined} 
                                                onClick={() => setShowSignOut(true)}
                                            />
                                        )}
                                        { showSignOut === true && (
                                            <Callout target={`.${styles.signOut} div`} onDismiss={() => setShowSignOut(false)}>
                                                <PrimaryButton iconProps={{iconName: "SignOut"}} text="Sign Out" href="/MicrosoftIdentity/Account/SignOut" />
                                            </Callout>
                                        )}
                                    </div>
                                    <div className={styles.logo}>
                                        <img src="/api/logo" alt="Organisation logo" />
                                    </div>
                                </div>
                                <div className={styles.menuRow}>
                                    <TooltipHost content="Click here to view the status of your bookings (desks and rooms)">
                                        <button onClick={() => history.push("/menu/mybookings")}>
                                            <img src={myBookingsImg} alt="Man sitting at desk" />
                                            <div>My Bookings</div>
                                        </button>
                                    </TooltipHost>
                                    { profileAndSettings?.Settings.DeskBookingEnabled === true && (
                                        <TooltipHost content="Click here to book a desk">
                                            <button disabled={profileAndSettings.Settings.MaintenanceMode} onClick={() => history.push("/menu/book/desk")}>
                                                <img src={bookDeskImg} alt="Man looking at a calendar" />
                                                <div>Book a Desk</div>
                                            </button>
                                        </TooltipHost>
                                    )}
                                    { profileAndSettings?.Settings.RoomBookingEnabled === true && (
                                        <TooltipHost content="Click here to find a room or view room calendars">
                                            <button disabled={profileAndSettings.Settings.MaintenanceMode} onClick={() => history.push("/menu/book/room")}>
                                                <img src={inOfficeImg} alt="Two people sitting in an office" />
                                                <div>Book a Room</div>
                                            </button>
                                        </TooltipHost>
                                    )}
                                    { profileAndSettings?.Settings.RoomBookingEnabled === true && profileAndSettings?.Profile.RoomApprover === true && pendingBookingCount > 0 && (
                                        <TooltipHost content="Click here to action pending room booking requests">
                                            <button onClick={() => history.push("/menu/approvals")}>
                                                <img src={approvals} alt="Man looking at requests for approval" />
                                                <div>Approve Requests <span className={styles.approvals}>{ pendingBookingCount }</span></div>
                                            </button>
                                        </TooltipHost>
                                    )}
                                    { profileAndSettings?.Settings.RoomBookingEnabled === true && profileAndSettings?.Profile.SiteLvlAdmin === true && (
                                        <TooltipHost content="Click to view site bookings">
                                            <button onClick={() => history.push("/menu/calendar")}>
                                                <img src={calendar} alt="Woman standing in front of calendar" />
                                                <div>Reception View</div>
                                            </button>
                                        </TooltipHost>
                                    )}
                                    <TooltipHost content="Click here to view or amend your profile">
                                        <button onClick={() => history.push("/menu/myprofile")}>
                                            <img src={profileImg} alt="A user profile" />
                                            <div>My Profile</div>
                                        </button>
                                    </TooltipHost>
                                    <TooltipHost content={!(!profileAndSettings || (profileAndSettings && !profileAndSettings.Profile?.RoomApprover && profileAndSettings.Profile?.Roles === null)) ? "Click here to view settings, configure sites/rooms, and manage desk bookings" : undefined}>
                                        <button disabled={!profileAndSettings || (profileAndSettings && !profileAndSettings.Profile?.RoomApprover && profileAndSettings.Profile?.Roles === null)} onClick={() => {
                                            history.push("/menu/settings");
                                        }}>
                                            <img src={settingsImg} alt="Various Settings" />
                                            <div>Settings</div>
                                        </button>
                                    </TooltipHost>
                                </div>
                            </div>
                        </Route>
                    </div>
                    <Route path="/menu/tagTesting" exact={true}>
                        <TagPicker label="Choose a Tag" type={TagType.User} onTagsRemoved={(tags) => {
                            console.log('Tags removed', tags);
                        }} />
                    </Route>
                    <Route path="/menu/mybookings" exact={true}>
                        <MyBookingsPanel onDismiss={() => history.goBack()} />
                    </Route>
                    <Route path="/menu/approvals">
                        <PendingApproval onDismiss={() => history.goBack() }/>
                    </Route>
                    <Route path="/menu/book/desk" exact={true}>
                        <BookingDatePanel onDismiss={() => history.goBack()} />
                    </Route>
                    <Route path="/menu/book/room" exact={true}>
                        <RoomBookingTop onDismiss={() => history.push("/menu")} />
                        {/* <BookingDatePanel type="room" onDismiss={() => history.goBack()} /> */}
                    </Route>
                    <Route path="/menu/book/desk/:startDateStr/:endDateStr/:siteId/:locationId/:floorplanId" exact={true} component={(route: any) => {
                        return <DeskBookingForm {...route.match.params} onDismiss={() => history.push("/menu")} />;
                    }} />
                    <Route path="/menu/book/room/:startDateStr/:endDateStr/:siteId/:locationId" exact={true} component={(route: any) => (
                        // <BookingRoomAvailability {...route.match.params} onDismiss={() => history.push("/menu")} />
                        <></>
                    )} />
                    <Route path="/menu/book/desk/:startDateStr/:endDateStr/:siteId/:locationId" exact={true} component={(route: any) => (
                        <BookingFloorplanPanel {...route.match.params} onDismiss={() => history.push("/menu")} /> 
                    )} />
                    <Route path="/menu/book/desk/:startDateStr/:endDateStr/:siteId" exact={true} component={(route: any) => (
                        <BookingLocationPanel startDateStr={route.match.params.startDateStr} endDateStr={route.match.params.endDateStr} siteId={route.match.params.siteId} onDismiss={() => history.push("/menu")} />
                    )} />
                    <Route path="/menu/book/desk/:startDateStr/:endDateStr" exact={true} component={(route: any) => {
                        return <BookingSitePanel startDateStr={route.match.params.startDateStr} endDateStr={route.match.params.endDateStr} onDismiss={() => history.push("/menu")} />;
                    }} />
                    <Route path="/menu/calendar" exact={true} component={(route: any) => {
                        return <SiteCalendar onDismiss={() => history.goBack()} />;
                    }} />
                    <Route path="/menu/myprofile">
                        { profileAndSettings !== undefined && (
                            <ProfileEditorPanel 
                                profile={profileAndSettings.Profile} 
                                onDismiss={() => {
                                    history.goBack();
                                }} 
                                onSaved={(profile) => setProfileAndSettings({...profileAndSettings, Profile: profile})}
                            />
                        )}
                    </Route>
                    <Route path="/menu/settings/">
                        <SettingsPanel profile={profileAndSettings.Profile} onDismiss={() => {
                            history.goBack();
                        }} />
                    </Route>
                    {notificationsDefault === true && (
                        <Dialog
                            hidden={false}
                            isOpen={true}
                            dialogContentProps={{
                                title: "Allow notifications"
                            }}
                        >
                            <div>Turn on notifications to receive messages and reminders.</div>
                            <DialogFooter>
                                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 20 }}>
                                    <PrimaryButton iconProps={{ iconName: "Ringer" }} onClick={() => {
                                        notifications.RequestPermission(() => {
                                            setNotificationsDefault(false);
                                        });
                                    } }>Turn on</PrimaryButton>
                                    <DefaultButton iconProps={{ iconName: "Cancel" }} onClick={() => {
                                        notifications.SetSnoozeCookie();
                                        setNotificationsDefault(false);
                                    } }>No thanks</DefaultButton>
                                </Stack>
                            </DialogFooter>
                        </Dialog>
                    ) }
                </div>
            </ProfileContext.Provider>
        </CacheBuster>
    );
}